// Jour de double match (D-092) : deux équipes engagées d'un même groupe (SF1, SF2) jouent le même
// jour. Le staff répartit les joueuses : l'une, l'autre ou les deux (« doubler »). Doubler reste
// toujours possible (le coach décide) ; l'écran prévient quand l'écart entre les coups d'envoi ne
// couvre pas le match, l'échauffement et la route. Logique pure : route entre les deux salles,
// écart, choix de chacune, compteurs et libellés accordés au genre de l'équipe.
import { agree, type TeamGender } from './labels';
import { inSquad } from './squads';
import { duration } from './trip';

/** Un match et son échauffement : il faut 2 h entre deux coups d'envoi, route en plus. */
export const MATCH_BLOCK_MIN = 120;
/** Joueuses sur une feuille de match : un repère pour le compteur, pas une règle ; réglable par équipe, 12 par défaut. */
export const SHEET_MAX = 12;
/** Le repère d'une équipe engagée (SF1…), sinon celui du groupe, sinon 12. */
export const sheetMaxOf = (team?: { sheet_max?: number | null } | null, squad?: { sheet_max?: number | null } | null): number =>
  squad?.sheet_max ?? team?.sheet_max ?? SHEET_MAX;

export interface Point { lat: number; lng: number }

/** Distance à vol d'oiseau, en km. */
export function crowKm(a: Point, b: Point): number {
  const rad = (d: number) => (d * Math.PI) / 180;
  const h = Math.sin(rad(b.lat - a.lat) / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(rad(b.lng - a.lng) / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

/** Minutes de route estimées quand l'itinéraire est indisponible (vol d'oiseau × 1,3 à 70 km/h ; même salle : 0). */
export const roadEstimate = (km: number) => (km < 0.3 ? 0 : Math.ceil((km * 1.3 * 60) / 70));

/** La salle d'un des deux matchs : à domicile ou non, ses coordonnées, et le temps de route en cache
 *  depuis notre gymnase (match à l'extérieur, D-053). */
export interface Venue { home: boolean | null; at: Point | null; cachedMin: number | null }

/** Comment connaître la route entre les deux salles : un match à domicile et
 *  l'autre à l'extérieur → le trajet en cache ; deux salles localisées → l'itinéraire (ou l'estimation) ;
 *  deux matchs à domicile → 0 ; sinon inconnue. */
export type TravelPlan = { min: number | null } | { route: [Point, Point] };
export function travelPlan(a: Venue, b: Venue): TravelPlan {
  if (a.home && b.home === false && b.cachedMin != null) return { min: Math.ceil(b.cachedMin) };
  if (b.home && a.home === false && a.cachedMin != null) return { min: Math.ceil(a.cachedMin) };
  if (a.at && b.at) return crowKm(a.at, b.at) < 0.3 ? { min: 0 } : { route: [a.at, b.at] };
  if (a.home && b.home) return { min: 0 };
  return { min: null };
}

export interface Feasibility { ok: boolean; gap: number; need: number; travel: number | null }

/** Doubler confortablement : l'écart entre les deux coups d'envoi couvre 2 h (match, échauffement)
 *  plus la route (inconnue : comptée 0, et l'écran le dit). Sinon l'écran prévient, sans interdire. */
export function feasibility(startA: Date, startB: Date, travel: number | null): Feasibility {
  const gap = Math.floor(Math.abs(startB.getTime() - startA.getTime()) / 60_000);
  const need = MATCH_BLOCK_MIN + (travel ?? 0);
  return { ok: gap >= need, gap, need, travel };
}

/** « 7 h 30 », « 2 h », « 45 min ». */
export const hm = (min: number) => (min < 60 ? `${min} min` : `${Math.floor(min / 60)} h${min % 60 ? ` ${String(min % 60).padStart(2, '0')}` : ''}`);

export interface DaySideLite { opponent: string; home: boolean | null }

/** Le bandeau de l'écran « Répartir » : possible, ou « Attention » et pourquoi. `first` joue le premier. */
export function doubleNote(f: Feasibility, first: DaySideLite, second: DaySideLite): { ok: boolean; lead: string; detail: string } {
  if (f.ok) {
    const road = f.travel === null ? ' (temps de route inconnu : vérifie)'
      : f.travel === 0 ? (first.home && second.home ? ', même salle' : '')
      : first.home === false && second.home ? `, retour de ${first.opponent} compris`
      : first.home && second.home === false ? `, trajet vers ${second.opponent} compris`
      : ', trajet entre les deux salles compris';
    return { ok: true, lead: 'Doubler est possible', detail: `${hm(f.gap)} entre les deux matchs${road}.` };
  }
  const road = f.travel ? `, moins ${duration(f.travel)} de route et l’échauffement` : ` : il faut ${hm(MATCH_BLOCK_MIN)} pour le match et l’échauffement`;
  return { ok: false, lead: 'Attention', detail: `${hm(f.gap)} entre les deux coups d’envoi${road}.` };
}

/** Où joue une joueuse ce jour-là : `a` (la première équipe du groupe, SF1), `b` (SF2), les deux, ou à placer. */
export type Pick = 'a' | 'b' | 'both' | null;

/** L'équipe où elle joue d'habitude (D-042) : une seule des deux → celle-là ; les deux → à placer. */
export function usualPick(squadIds: readonly string[] | null | undefined, sqA: string, sqB: string): Pick {
  const inA = inSquad(squadIds, sqA), inB = inSquad(squadIds, sqB);
  return inA && !inB ? 'a' : inB && !inA ? 'b' : null;
}

/** Le choix en cours, d'après les deux compositions ; rien d'écrit pour elle : son équipe habituelle. */
export function currentPick(rowA: boolean | undefined, rowB: boolean | undefined, usual: Pick): Pick {
  if (rowA && rowB) return 'both';
  if (rowA) return 'a';
  if (rowB) return 'b';
  if (rowA === false || rowB === false) return null;
  return usual;
}

/** Disponible pour chacun des deux matchs : présente au match où elle est convoquée ; à l'autre, où
 *  elle n'est pas convoquée d'habitude, comme au sien. */
export function availability(conv: { a: boolean; b: boolean }, present: { a: boolean; b: boolean }): { a: boolean; b: boolean } {
  return {
    a: conv.a ? present.a : conv.b ? present.b : false,
    b: conv.b ? present.b : conv.a ? present.a : false,
  };
}

export interface DayPlayerState {
  /** Joueuse du groupe (sinon : renfort convoquée ce jour-là, seulement dans son match). */
  member: boolean;
  conv: { a: boolean; b: boolean };
  avail: { a: boolean; b: boolean };
  /** Brûlée : la seule équipe où elle joue. */
  lock: 'a' | 'b' | null;
}

/** Les choix ouverts à une joueuse : SF1, SF2, et les deux dès qu'elle peut jouer l'un et l'autre
 *  (brûlée, absente à un match, renfort d'un seul match : grisés). */
export function choices(p: DayPlayerState): { a: boolean; b: boolean; both: boolean } {
  const a = p.avail.a && p.lock !== 'b' && (p.member || p.conv.a);
  const b = p.avail.b && p.lock !== 'a' && (p.member || p.conv.b);
  return { a, b, both: a && b };
}

/** Le choix affiché : l'équipe habituelle seulement si elle est ouverte (sinon : à placer). */
export function shownPick(rowA: boolean | undefined, rowB: boolean | undefined, usual: Pick, open: { a: boolean; b: boolean }): Pick {
  const written = currentPick(rowA, rowB, null);
  if (written || rowA !== undefined || rowB !== undefined) return written;
  return (usual === 'a' || usual === 'b') && open[usual] ? usual : null;
}

/** Les compteurs : joueuses de chaque feuille (celles qui doublent comptent dans les deux), qui double, à placer. */
export function dayCounts(picks: readonly Pick[]): { a: number; b: number; both: number; todo: number } {
  return {
    a: picks.filter(p => p === 'a' || p === 'both').length,
    b: picks.filter(p => p === 'b' || p === 'both').length,
    both: picks.filter(p => p === 'both').length,
    todo: picks.filter(p => p === null).length,
  };
}

/** Les retenues d'un match, le même compte partout (D-103) : la composition, « À faire », les stats et
 *  « Répartir ». Ce sont les lignes cochées de la composition, sauf une absente du jour ; une joueuse
 *  placée depuis l'autre équipe du groupe (double match) compte dès qu'elle est placée. */
export function retainedOf(lineup: readonly { person_id: string; selected: boolean }[], absent: Iterable<string> = []): string[] {
  const off = new Set(absent);
  return lineup.filter(l => l.selected && !off.has(l.person_id)).map(l => l.person_id);
}

/** Celles qui n'ont encore rien d'écrit dans les deux compositions, et le choix que l'écran leur montre :
 *  à enregistrer tout de suite, pour qu'il n'y ait qu'un état (D-103). */
export function picksToSave<P extends { pid: string; rowA?: boolean; rowB?: boolean }>(players: readonly P[], shown: (p: P) => Pick): { pid: string; pick: Exclude<Pick, null> }[] {
  return players.flatMap(p => {
    const pick = shown(p);
    return p.rowA === undefined && p.rowB === undefined && pick ? [{ pid: p.pid, pick }] : [];
  });
}

/** « 1 de plus que la feuille », « 2 de plus que la feuille » ; rien en dessous du repère. */
export const overSheet = (n: number, max: number) => (n > max ? `${n - max} de plus que la feuille` : '');

/** Les matchs qu'elle joue, pour la base : [] (à placer), [a], [b] ou [a, b]. */
export const pickEvents = (pick: Pick, a: string, b: string): string[] =>
  pick === 'both' ? [a, b] : pick === 'a' ? [a] : pick === 'b' ? [b] : [];

/** Sous le prénom : « présente · SF1 seulement (brûlée) », « présent · à placer »… */
export function playerNote(g: TeamGender, x: { lockName?: string | null; todo?: boolean; renfort?: boolean; onlyFor?: string | null }): string {
  const parts = [agree(g, 'présent', 'présente')];
  if (x.renfort) parts.push('renfort');
  if (x.onlyFor) parts.push(`${agree(g, 'absent', 'absente')} au match ${x.onlyFor}`);
  if (x.lockName) parts.push(`${x.lockName} seulement (${agree(g, 'brûlé', 'brûlée')})`);
  if (x.todo) parts.push('à placer');
  return parts.join(' · ');
}

/** « Absentes samedi : Jade (médical), Lola. » */
export function absentLine(g: TeamGender, day: string, list: readonly { name: string; why: string | null }[]): string {
  if (!list.length) return '';
  return `${agree(g, 'Absent', 'Absente', list.length)} ${day} : ${list.map(x => (x.why ? `${x.name} (${x.why})` : x.name)).join(', ')}.`;
}

/** Les puces de la carte du match : « SF1 · 8 », « SF2 · 7 », « 2 doublent », « 2 à placer ». */
export function countChips(c: ReturnType<typeof dayCounts>, nameA: string, nameB: string): { label: string; kind: 'ok' | 'both' | 'todo' }[] {
  const out: { label: string; kind: 'ok' | 'both' | 'todo' }[] = [{ label: `${nameA} · ${c.a}`, kind: 'ok' }, { label: `${nameB} · ${c.b}`, kind: 'ok' }];
  if (c.both) out.push({ label: `${c.both} ${c.both > 1 ? 'doublent' : 'double'}`, kind: 'both' });
  if (c.todo) out.push({ label: `${c.todo} à placer`, kind: 'todo' });
  return out;
}

/** Un match vu pour un renfort : son heure, sa salle, la route en cache depuis notre gymnase. */
export interface GameLite { start: Date; home: boolean | null; at: Point | null; tripMin: number | null; opponent: string }

/** Renfort qui joue aussi le même jour (Idries : « comme pour la répartition entre les 2 équipes ») : le même calcul
 *  que pour doubler (2 h + la route ; deux salles localisées : l'estimation de route). */
export function renfortDouble(target: GameLite, game: GameLite): { ok: boolean; lead: string; detail: string } {
  const venue = (g: GameLite): Venue => ({ home: g.home, at: g.at, cachedMin: g.tripMin });
  const plan = travelPlan(venue(target), venue(game));
  const travel = 'route' in plan ? roadEstimate(crowKm(...plan.route)) : plan.min;
  const [first, second] = target.start <= game.start ? [target, game] : [game, target];
  return doubleNote(feasibility(first.start, second.start, travel), first, second);
}

/** « domicile », « extérieur, 45 min de route » (« ≈ » : estimée à vol d'oiseau), « terrain neutre ». */
export const gameWhere = (g: { home: boolean | null; tripMin: number | null }, estimated = false): string =>
  g.home ? 'domicile' : g.home === false ? (g.tripMin ? `extérieur, ${estimated ? '≈ ' : ''}${duration(Math.ceil(g.tripMin))} de route` : 'extérieur') : 'terrain neutre';
